import type { StorePublicProduct } from '@evi/shared';
import { SHIPPING_RATE_CENTS, shippingClassFor, supplierPartNumber } from '@evi/shared';
import type { ProductFamily } from '@evi/store-kit';
import { productPath, SHOP_TIER_LABEL } from '@evi/store-kit';

/**
 * What a product page says about one SKU, shared by the page itself and its
 * structured data so the two cannot drift (Google rejects a listing whose
 * markup disagrees with the page).
 *
 * The catalog carries a name, a description and a lead time, and its structured
 * `specs` field is empty today. So the spec rows below restate what each SKU's
 * own catalog name and description already say, nothing more; specs.test.ts
 * checks that against a copy of the catalog. A SKU added later still gets the
 * generic rows (brand, part number, category) and whatever `specs` it carries.
 */

export interface SpecRow { label: string; value: string }

/** Pure text for the six SKUs whose catalog description is empty. A description
 *  in the catalog always wins. */
const DESCRIPTION_FALLBACK: Record<string, string> = {
  RFID: 'Two RFID charge cards per package, for starting a charge on the EV Initiative network.',
  UP10: 'Pedestal post for mounting an AC wallbox charger.',
  DCPP: 'Pedestal post for DC charger installations.',
  WMKIT: 'Wall mount bracket kit for the DC Compact charger.',
  US480A2L0: '480 kW DC fast charging full set: the 480 kW unit, 2 Boost dispensers and 2 CMS. Sold by quote.',
  US640A2L0: '640 kW DC fast charging full set: the 640 kW unit, 2 Boost dispensers and 2 CMS. Sold by quote.',
};

/** The catalog description, else the family's blurb, else the text above, else
 *  the product's own name. Every product page has one. */
export function productDescription(product: StorePublicProduct, family: ProductFamily): string {
  return product.description?.trim()
    || DESCRIPTION_FALLBACK[product.part_number]
    || family.blurb
    || `${product.name}, from the EV Initiative Store.`;
}

/** One line of body text, for a meta description or structured data. */
export function plainDescription(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** The RFID card is EVI's own. Everything else in the catalog is Autel hardware
 *  or an Autel part (Product.ts: part_number is "the Autel SKU", drop-shipped
 *  through Autel). */
export function productBrand(product: StorePublicProduct): string {
  return product.part_number === 'RFID' ? 'EV Initiative' : 'Autel';
}

const LEVEL_2 = { label: 'Charging', value: 'Level 2 AC' };
const DC_FAST = { label: 'Charging', value: 'DC fast' };
const row = (label: string, value: string): SpecRow => ({ label, value });

const eliteHome = (power: string, output: string, connection: string): SpecRow[] => [
  row('Power', power), LEVEL_2, row('Output', output), row('Connection', connection),
  row('Warranty', '3-year manufacturer warranty'),
  row('Network', 'Does not connect to the EV Initiative network'),
];
const appStart = row('Starts with', 'EVI Driver App, RFID card or QR code');
const acUltra = (ports: string, mount: string, extra: SpecRow[] = []): SpecRow[] => [
  row('Power', '19.2 kW'), LEVEL_2, row('Output', '6A to 80A'), row('Ports', ports), row('Mount', mount),
  row('Cable', '25 ft'), ...extra, row('Warranty', '3-year manufacturer warranty'), appStart,
];
const dcWarranty = row('Warranty', '2-year manufacturer warranty standard');
const dcConnectors = row('Connectors', 'CCS1 or NACS on each plug');
const dcIncluded = row('Included', 'Pre-commissioning to the network');
const dcCompact = (mount: string): SpecRow[] => [
  row('Power', '40 kW'), DC_FAST, row('Plugs', 'Dual CCS'), dcConnectors, row('Mount', mount), row('Cable', '25 ft'),
  dcIncluded, dcWarranty,
];
const maxi = (power: string): SpecRow[] => [
  row('Power', power), DC_FAST, dcConnectors, row('Cable management', 'Integrated'), dcIncluded, dcWarranty,
];
const boost = (power: string): SpecRow[] => [
  row('Power', power), DC_FAST, row('Cable', 'Boost'), dcConnectors, row('Cable management', 'Integrated'), dcIncluded,
  dcWarranty,
];
const fullSet = (power: string): SpecRow[] => [
  row('Power', power), DC_FAST, row('Dispensers', '2 Boost dispensers'), row('Also in the set', '2 CMS'),
];

/** Keyed on part number, the catalog's natural key. */
const SPECS_BY_PART: Record<string, SpecRow[]> = {
  MC40AP6I: eliteHome('9.6 kW', '6A to 40A', 'NEMA 6-50 plug'),
  MC40AP14I: eliteHome('9.6 kW', '6A to 40A', 'NEMA 14-50 plug'),
  MC50AHI: eliteHome('12 kW', '6A to 50A', 'Hardwired'),
  'MCC50AHI-HOME': [
    row('Power', '12 kW'), LEVEL_2, row('Output', '6A to 50A'), row('Holster', 'In-body'),
    row('Connection', 'Hardwired, installed by a licensed electrician'),
    row('Network', 'Connects to the EV Initiative network over home Wi-Fi'),
    row('Warranty', '3-year manufacturer warranty'),
  ],
  MCC50AHI: [
    row('Power', '12 kW'), LEVEL_2, row('Output', '6A to 50A'), row('Holster', 'In-body'),
    row('Warranty', '3-year manufacturer warranty'), appStart,
  ],
  UW19C002: [
    row('Power', '19.2 kW'), LEVEL_2, row('Output', '6A to 80A'), row('Holster', 'In-body'),
    row('Warranty', '5-year manufacturer warranty'), appStart,
  ],
  UF19L001: acUltra('Single port', 'Wall'),
  UF38L001: acUltra('Dual port', 'Wall'),
  'MU38FN7N-CMSA2': acUltra('Dual port', 'Pedestal', [row('Also in the set', 'Pedestal and CMS')]),
  DC40AWN25: dcCompact('Wall'),
  DC40APN25: dcCompact('Pedestal'),
  UF060A3001: maxi('60 kW'),
  UF080A3001: maxi('80 kW'),
  UF100A3001: maxi('100 kW'),
  UF120A3001: maxi('120 kW'),
  UF060A4001: boost('60 kW'),
  UF120A4001: boost('120 kW'),
  UF180A4001: boost('180 kW'),
  UF240A4001: boost('240 kW'),
  US480A2L0: fullSet('480 kW'),
  US640A2L0: fullSet('640 kW'),
  RFID: [row('In the package', '2 RFID cards')],
  UP10: [row('Fits', 'AC wallbox')],
  WMKIT: [row('Fits', 'DC Compact')],
};

/** Rows from the catalog's structured field, for a SKU that carries it. */
function structuredSpecs(product: StorePublicProduct): SpecRow[] {
  const s = product.specs;
  if (!s) return [];
  const rows: SpecRow[] = [];
  if (s.power_kw) rows.push(row('Power', `${s.power_kw} kW`));
  if (s.amperage) rows.push(row('Output', `${s.amperage}A`));
  if (s.connector_type) rows.push(row('Connector', s.connector_type));
  if (s.ports) rows.push(row('Ports', String(s.ports)));
  if (s.mount) rows.push(row('Mount', s.mount));
  return rows;
}

/** The Specifications block on a product page. */
export function productSpecs(product: StorePublicProduct): SpecRow[] {
  const tier = SHOP_TIER_LABEL[product.tier];
  return [
    row('Brand', productBrand(product)),
    row('Part number', product.part_number),
    ...(tier ? [row('Category', tier)] : []),
    ...(SPECS_BY_PART[product.part_number] ?? structuredSpecs(product)),
  ];
}

export interface ProductAvailability {
  /** What the page says. */
  label: string;
  /** What the structured data says. */
  schema: string;
}

/** The catalog has no inventory (drop-ship through Autel), so "can it be ordered
 *  now" is the only fact there is. Anything checkout accepts is available to
 *  order, with its lead time beside it. A quote-flow product is rejected by
 *  checkout (routes/store/checkout.ts), so it reads as made to order. */
export function productAvailability(product: StorePublicProduct): ProductAvailability {
  return product.checkout_flow === 'buy_now'
    ? { label: 'Available to order', schema: 'https://schema.org/InStock' }
    : { label: 'Made to order, by quote', schema: 'https://schema.org/MadeToOrder' };
}

/** Whether the page can sell it. A quote-flow product talks to sales instead. */
export function isBuyable(product: StorePublicProduct): boolean {
  return product.checkout_flow === 'buy_now';
}

/** Days from a lead-time label ("Ships in 3-5 business days", "Ships freight in
 *  2-3 weeks"). Null when the label reads any other way. */
export function handlingDays(label: string | undefined): { min: number; max: number } | null {
  const m = label?.match(/(\d+)\s*-\s*(\d+)\s*(business days?|days?|weeks?)/i);
  if (!m) return null;
  const unit = /week/i.test(m[3]!) ? 7 : 1;
  return { min: Number(m[1]) * unit, max: Number(m[2]) * unit };
}

/**
 * Structured shipping for a SKU, only where the page prints a firm number. DC
 * freight prints "(estimate, confirmed on order)", so it stays out, and a quote
 * has no shipping line at all. Destinations follow the checkout's own allowed
 * countries (lib/freight/storeFreight.ts).
 */
export function offerShippingDetails(product: StorePublicProduct): Record<string, unknown> | undefined {
  if (!isBuyable(product)) return undefined;
  const cls = shippingClassFor({ part_number: product.part_number, name: product.name, tier: product.tier });
  if (cls === 'dc') return undefined;
  const days = handlingDays(product.lead_time_label);
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: (SHIPPING_RATE_CENTS[cls] / 100).toFixed(2),
      currency: 'USD',
    },
    shippingDestination: { '@type': 'DefinedRegion', addressCountry: ['US', 'CA'] },
    ...(days
      ? {
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: days.min, maxValue: days.max, unitCode: 'DAY' },
          },
        }
      : {}),
  };
}

/** Schema.org Product + Offer for one product page. Every value here is on the
 *  page: name and part number under the title, the description, the image, the
 *  price, the availability line and the shipping line. */
export function productJsonLd(
  product: StorePublicProduct,
  family: ProductFamily,
  origin: string,
): Record<string, unknown> {
  const url = `${origin}${productPath(product)}`;
  const brand = productBrand(product);
  const shipping = offerShippingDetails(product);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    ...(family.media?.hero ? { image: [`${origin}${family.media.hero}`] } : {}),
    description: plainDescription(productDescription(product, family)),
    sku: product.part_number,
    ...(brand === 'Autel' ? { mpn: supplierPartNumber(product.part_number) } : {}),
    brand: { '@type': 'Brand', name: brand },
    offers: {
      '@type': 'Offer',
      url,
      price: (product.price_cents / 100).toFixed(2),
      priceCurrency: 'USD',
      availability: productAvailability(product).schema,
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'EV Initiative' },
      ...(shipping ? { shippingDetails: shipping } : {}),
    },
  };
}
